import { useState, useCallback, useEffect, useRef } from "react";
import { RLGame } from "./RLGame";
import { ParametersPanel } from "./ParametersPanel";
import { LevelProgressBar } from "./LevelProgressBar";
import { LevelUnlocksCard } from "./LevelUnlocksCard";
import { useLanguage } from "@/contexts/LanguageContext";
import { LevelProvider } from "@/contexts/LevelContext";
import { Button } from "@/components/ui/button";
import { getUnlockedFeatures } from "@/lib/levelProgression";
import type { LevelNumber } from "@/lib/levelProgression";

export function RLGameNew() {
  const { translate } = useLanguage();
  const [explorationRate, setExplorationRate] = useState(0.2);
  const [alpha, setAlpha] = useState(0.1);
  const [gamma, setGamma] = useState(0.85);
  const [mode, setMode] = useState<"levels" | "free">("levels");
  const [currentLevel, setCurrentLevel] = useState<LevelNumber>(1);
  const [freeModeUnlocked, setFreeModeUnlocked] = useState(false);
  // Highest level the player has unlocked by solving the previous one.
  // Reaches 11 once level 10 is solved, which unlocks Free Mode.
  const [maxUnlockedLevel, setMaxUnlockedLevel] = useState(1);

  const MAX_UNLOCK_KEY = "rr-max-unlocked-level";
  const FREE_MODE_KEY = "rr-free-mode-unlocked";

  // Restore saved progress on mount (after hydration to avoid SSR mismatch).
  useEffect(() => {
    if (typeof window === "undefined") return;
    const storedUnlock = window.localStorage.getItem(MAX_UNLOCK_KEY);
    if (storedUnlock) {
      const n = parseInt(storedUnlock, 10);
      if (!Number.isNaN(n) && n >= 1) setMaxUnlockedLevel(Math.min(11, n));
    }
    if (window.localStorage.getItem(FREE_MODE_KEY) === "true") {
      setFreeModeUnlocked(true);
    }
  }, []);

  // Keep the latest level in a ref so the (stable) solve handler always sees it.
  const currentLevelRef = useRef(currentLevel);
  useEffect(() => {
    currentLevelRef.current = currentLevel;
  }, [currentLevel]);

  // Called from RLGame when the rover reaches the goal in Level Mode.
  const handleLevelSolved = useCallback(() => {
    setMaxUnlockedLevel((prev) => {
      const next = Math.min(11, Math.max(prev, currentLevelRef.current + 1));
      if (next !== prev && typeof window !== "undefined") {
        window.localStorage.setItem(MAX_UNLOCK_KEY, String(next));
      }
      return next;
    });
  }, []);

  // Cheat code: type "blauwal" to unlock Free Mode
  useEffect(() => {
    let keySequence = "";
    const handleKeyPress = (e: KeyboardEvent) => {
      keySequence += e.key.toLowerCase();
      if (keySequence.length > 10) {
        keySequence = keySequence.slice(-10);
      }
      if (keySequence.includes("blauwal")) {
        setFreeModeUnlocked(true);
        if (typeof window !== "undefined") {
          window.localStorage.setItem(FREE_MODE_KEY, "true");
        }
        keySequence = "";
      }
    };

    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, []);

  const canAccessFreeMode = freeModeUnlocked || maxUnlockedLevel > 10;
  const isLevelMode = mode === "levels";
  const unlockedFeatures = getUnlockedFeatures(currentLevel);
  // The next level is only reachable once the current one has been solved.
  const nextLevelLocked = currentLevel >= maxUnlockedLevel;

  const goToLevel = (level: number) => {
    const clamped = Math.max(1, Math.min(10, level)) as LevelNumber;
    // Never allow jumping past the highest unlocked level.
    if (clamped > maxUnlockedLevel) return;
    setCurrentLevel(clamped);
  };

  // Wipes saved level/free-mode progress (e.g. from earlier testing) so
  // locking can be re-verified from a clean slate without opening DevTools.
  const resetProgress = () => {
    if (typeof window !== "undefined") {
      window.localStorage.removeItem(MAX_UNLOCK_KEY);
      window.localStorage.removeItem(FREE_MODE_KEY);
    }
    setMaxUnlockedLevel(1);
    setFreeModeUnlocked(false);
    setCurrentLevel(1);
  };

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      {/* Mode Selector */}
      <div className="border-b border-border/40 bg-card/30 px-8 py-4 sticky top-0 z-30">
        <div className="flex gap-3">
          <Button
            variant={mode === "levels" ? "default" : "outline"}
            onClick={() => setMode("levels")}
            className="font-semibold"
          >
            {translate("Level-Modus", "Level Mode")}
          </Button>
          <Button
            variant={mode === "free" ? "default" : "outline"}
            onClick={() => setMode("free")}
            className="font-semibold"
            disabled={!canAccessFreeMode && mode !== "free"}
          >
            {translate("Free Mode", "Free Mode")}
            {!canAccessFreeMode && <span className="ml-2 text-xs">🔒</span>}
          </Button>
        </div>
      </div>

      {/* Main Content - 2 Column Layout */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left Sidebar - Parameters & Progress */}
        <div className="w-72 flex-shrink-0 border-r border-border/40 bg-card/20 p-6 overflow-y-auto">
          {!isLevelMode && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setMode("levels")}
                className="w-full font-semibold mb-6"
              >
                ← {translate("Zurück zu Level-Modus", "Back to Level Mode")}
              </Button>
            </>
          )}
          {isLevelMode && (
            <>
              <LevelProgressBar currentLevel={currentLevel} translate={translate} />
              <div className="mt-3 flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => goToLevel(currentLevel - 1)}
                  disabled={currentLevel <= 1}
                  className="flex-1 font-semibold"
                >
                  ← {translate("Level", "Level")}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => goToLevel(currentLevel + 1)}
                  disabled={currentLevel >= 10 || nextLevelLocked}
                  className="flex-1 font-semibold"
                >
                  {translate("Level", "Level")} →
                  {nextLevelLocked && currentLevel < 10 && <span className="ml-1 text-xs">🔒</span>}
                </Button>
              </div>
              {nextLevelLocked && (
                <p className="mt-2 text-xs text-muted-foreground">
                  🔒{" "}
                  {currentLevel >= 10
                    ? translate(
                        "Erreiche das Ziel, um den Free Mode freizuschalten.",
                        "Reach the goal to unlock Free Mode.",
                      )
                    : translate(
                        "Bring den Rover ins Ziel, um das nächste Level freizuschalten.",
                        "Get the rover to the goal to unlock the next level.",
                      )}
                </p>
              )}
              <div className="mt-6" />
              <LevelUnlocksCard currentLevel={currentLevel} translate={translate} />
              {(maxUnlockedLevel > 1 || freeModeUnlocked) && (
                <button
                  type="button"
                  onClick={resetProgress}
                  className="mt-3 text-xs text-muted-foreground/70 hover:text-muted-foreground underline underline-offset-2"
                >
                  {translate("Fortschritt zurücksetzen", "Reset progress")}
                </button>
              )}
              <div className="mt-6" />
            </>
          )}
          <ParametersPanel
            explorationRate={explorationRate}
            alpha={alpha}
            gamma={gamma}
            onExplorationRateChange={setExplorationRate}
            onAlphaChange={setAlpha}
            onGammaChange={setGamma}
            isLevelMode={isLevelMode}
            canAdjustAlpha={unlockedFeatures.canAdjustAlpha}
            canAdjustGamma={unlockedFeatures.canAdjustGamma}
            translate={translate}
          />
        </div>

        {/* Right Content - Game */}
        <div className="flex-1 overflow-auto">
          <LevelProvider levelMode={isLevelMode} currentLevel={currentLevel} onLevelSolved={handleLevelSolved}>
            <RLGame explorationRate={explorationRate} alpha={alpha} gamma={gamma} />
          </LevelProvider>
        </div>
      </div>
    </div>
  );
}
